import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { detailedGallery } from '../detailedGalleryData';

const PhotoCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Extraire toutes les photos de toutes les catégories
  // detailedGallery is a static import and doesn't change at runtime
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const carouselPhotos = React.useMemo(() => {
    const photos = [];
    
    if (detailedGallery && typeof detailedGallery === 'object') {
      Object.values(detailedGallery).forEach(category => {
        if (category.subcategories) {
          Object.values(category.subcategories).forEach(subcategory => {
            if (subcategory.images && Array.isArray(subcategory.images)) {
              subcategory.images.forEach(image => {
                photos.push({
                  url: image.url,
                  title: image.title,
                  category: category.title
                });
              });
            }
          });
        }
      });
    }
    
    return photos;
  }, []);

  useEffect(() => {
    if (carouselPhotos.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === carouselPhotos.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(timer);
  }, [carouselPhotos.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? carouselPhotos.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === carouselPhotos.length - 1 ? 0 : currentIndex + 1);
  };

  const goToCategory = (category) => {
    const gallerySection = document.getElementById('galerie');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        // Convertir le nom de catégorie en ID
        const categoryMap = {
          'Bâtiment': 'batiment',
          'Peinture': 'peinture',
          'Ouvrages Techniques': 'ouvragesTechniques',
          'Événementiel': 'evenementiel',
          'Mise en Sécurité': 'securite',
          'Nettoyage': 'nettoyage',
          'Toiture & Couverture': 'toiture',
          'Descente Pluviale & Fumisterie': 'descentes',
          'Entretien & Maintenance': 'maintenance'
        };
        const domainId = categoryMap[category] || 'tous';
        const event = new CustomEvent('filterGallery', { detail: { domain: domainId } });
        window.dispatchEvent(event);
      }, 800);
    }
  };

  if (carouselPhotos.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gray-100">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-black mb-12">
          Nos <span className="text-orange-600">Réalisations</span>
        </h2>
        
        <div className="max-w-4xl mx-auto relative">
          {/* Diaporama */}
          <div className="relative aspect-[16/9] overflow-hidden rounded-2xl shadow-2xl bg-gray-900">
            {carouselPhotos.map((photo, index) => (
              <div
                key={`${photo.url}-${index}`}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div 
                  onClick={() => goToCategory(photo.category)}
                  className="cursor-pointer w-full h-full"
                >
                  <img
                    src={photo.url}
                    alt={photo.title}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-5">
                    <p className="text-orange-400 text-xs font-semibold mb-1 uppercase tracking-wide">{photo.category}</p>
                    <p className="text-white text-base font-semibold">{photo.title}</p>
                    <p className="text-gray-300 text-xs mt-1">Cliquez pour voir plus</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Boutons navigation */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-3 rounded-full shadow-lg transition-all z-10"
              aria-label="Photo précédente"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-3 rounded-full shadow-lg transition-all z-10"
              aria-label="Photo suivante"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Indicateurs */}
          <div className="flex justify-center gap-2 mt-6 flex-wrap">
            {carouselPhotos.slice(0, 40).map((photo, index) => (
              <button
                key={`indicator-${photo.url}-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-orange-600 w-6' 
                    : 'bg-gray-400 hover:bg-gray-500'
                }`}
                aria-label={`Aller à la photo ${index + 1}`}
              />
            ))}
          </div>
          
          <p className="text-center text-gray-600 mt-4">
            Photo {currentIndex + 1} / {carouselPhotos.length}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PhotoCarousel;
